import { useMemo } from "react";
import { CheckCircle2, AlertTriangle, XCircle } from "lucide-react";

interface SeoAnalyzerProps {
  title: string;
  slug: string;
  excerpt: string;
  content: string; // HTML
  focusKeyword: string; // comma-separated allowed
  featuredImage?: string;
}

type CheckStatus = "good" | "ok" | "bad";
interface Check {
  status: CheckStatus;
  label: string;
}

const stripHtml = (html: string) =>
  html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const countOccurrences = (text: string, kw: string) => {
  if (!kw) return 0;
  const re = new RegExp(kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
  return (text.match(re) || []).length;
};

const parseKeywords = (raw: string): string[] =>
  raw
    .split(",")
    .map((k) => k.trim())
    .filter(Boolean);

const buildKeywordChecks = (
  kw: string,
  ctx: {
    title: string;
    slug: string;
    excerpt: string;
    text: string;
    contentHtml: string;
    words: number;
  }
): Check[] => {
  const kwLower = kw.toLowerCase();
  const result: Check[] = [];
  const titleLower = ctx.title.toLowerCase();
  const slugLower = ctx.slug.toLowerCase();
  const excerptLower = ctx.excerpt.toLowerCase();

  result.push(
    titleLower.includes(kwLower)
      ? { status: "good", label: `Keyword "${kw}" found in the title.` }
      : { status: "bad", label: `Keyword "${kw}" missing from the title.` }
  );

  result.push(
    slugLower.includes(kwLower.replace(/\s+/g, "-"))
      ? { status: "good", label: `Keyword "${kw}" found in the URL slug.` }
      : { status: "ok", label: `Keyword "${kw}" missing from the URL slug.` }
  );

  result.push(
    excerptLower.includes(kwLower)
      ? { status: "good", label: `Keyword "${kw}" found in the excerpt.` }
      : { status: "bad", label: `Keyword "${kw}" missing from the excerpt.` }
  );

  const occ = countOccurrences(ctx.text, kw);
  if (occ === 0) {
    result.push({ status: "bad", label: `Keyword "${kw}" does not appear in the content.` });
  } else {
    result.push({ status: "good", label: `Keyword "${kw}" appears ${occ} time${occ === 1 ? "" : "s"} in the content.` });
    if (ctx.words > 0) {
      const density = (occ / ctx.words) * 100;
      if (density < 0.3) {
        result.push({ status: "ok", label: `"${kw}" density is low (${density.toFixed(2)}%).` });
      } else if (density > 3) {
        result.push({ status: "ok", label: `"${kw}" density is high (${density.toFixed(2)}%).` });
      } else {
        result.push({ status: "good", label: `"${kw}" density is good (${density.toFixed(2)}%).` });
      }
    }
  }

  const headingsText = (ctx.contentHtml.match(/<h[23][^>]*>([\s\S]*?)<\/h[23]>/gi) || [])
    .join(" ")
    .toLowerCase();
  if (headingsText) {
    result.push(
      headingsText.includes(kwLower)
        ? { status: "good", label: `Keyword "${kw}" found in a subheading.` }
        : { status: "ok", label: `Keyword "${kw}" missing from subheadings.` }
    );
  }

  return result;
};

const buildGeneralChecks = (
  title: string,
  excerpt: string,
  contentHtml: string,
  words: number,
  featuredImage?: string
): Check[] => {
  const result: Check[] = [];

  // Title length
  if (title.length === 0) result.push({ status: "bad", label: "Title is missing." });
  else if (title.length < 30) result.push({ status: "ok", label: `Title is short (${title.length} chars). Aim for 50–60.` });
  else if (title.length > 60) result.push({ status: "ok", label: `Title is long (${title.length} chars). Aim for 50–60.` });
  else result.push({ status: "good", label: `Title length is good (${title.length} chars).` });

  // Excerpt length
  if (!excerpt) result.push({ status: "bad", label: "Excerpt (meta description) is missing." });
  else if (excerpt.length < 120) result.push({ status: "ok", label: `Excerpt is short (${excerpt.length} chars). Aim for 120–155.` });
  else if (excerpt.length > 160) result.push({ status: "ok", label: `Excerpt is long (${excerpt.length} chars). Aim for 120–155.` });
  else result.push({ status: "good", label: `Excerpt length is good (${excerpt.length} chars).` });

  // Word count
  if (words < 300) result.push({ status: "ok", label: `Content is short (${words} words). Aim for 300+.` });
  else result.push({ status: "good", label: `Content length is good (${words} words).` });

  // Headings
  const h2h3 = (contentHtml.match(/<h[23][^>]*>/gi) || []).length;
  if (h2h3 === 0) result.push({ status: "ok", label: "No subheadings (H2/H3) found. Add some for structure." });
  else result.push({ status: "good", label: `${h2h3} subheading${h2h3 === 1 ? "" : "s"} found.` });

  // Images & alts
  const imgs = contentHtml.match(/<img[^>]*>/gi) || [];
  if (imgs.length === 0 && !featuredImage)
    result.push({ status: "ok", label: "No images found. Consider adding visuals." });
  else {
    const missingAlt = imgs.filter((i) => !/alt=["'][^"']+["']/i.test(i)).length;
    if (missingAlt > 0)
      result.push({ status: "bad", label: `${missingAlt} image(s) missing alt text.` });
    else if (imgs.length > 0)
      result.push({ status: "good", label: "All inline images have alt text." });
  }

  // Links
  const internal = (contentHtml.match(/<a[^>]+href=["'](\/|#)[^"']*["']/gi) || []).length;
  const external = (contentHtml.match(/<a[^>]+href=["']https?:\/\/[^"']+["']/gi) || []).length;
  result.push(
    internal > 0
      ? { status: "good", label: `${internal} internal link(s) found.` }
      : { status: "ok", label: "No internal links found." }
  );
  result.push(
    external > 0
      ? { status: "good", label: `${external} external link(s) found.` }
      : { status: "ok", label: "No external links found." }
  );

  return result;
};

const scoreOf = (checks: Check[]) => {
  if (checks.length === 0) return 0;
  const weights = { good: 1, ok: 0.5, bad: 0 };
  const total = checks.reduce((s, c) => s + weights[c.status], 0);
  return Math.round((total / checks.length) * 100);
};

const Icon = ({ s }: { s: CheckStatus }) =>
  s === "good" ? (
    <CheckCircle2 size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
  ) : s === "ok" ? (
    <AlertTriangle size={16} className="text-yellow-500 flex-shrink-0 mt-0.5" />
  ) : (
    <XCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
  );

const CheckList = ({ checks }: { checks: Check[] }) => (
  <ul className="space-y-2">
    {checks.map((c, i) => (
      <li key={i} className="flex gap-2 text-sm text-foreground/80">
        <Icon s={c.status} />
        <span>{c.label}</span>
      </li>
    ))}
  </ul>
);

export const SeoAnalyzer = ({
  title,
  slug,
  excerpt,
  content,
  focusKeyword,
  featuredImage,
}: SeoAnalyzerProps) => {
  const keywords = useMemo(() => parseKeywords(focusKeyword), [focusKeyword]);

  const text = useMemo(() => stripHtml(content), [content]);
  const words = useMemo(() => (text ? text.split(/\s+/).length : 0), [text]);

  const generalChecks = useMemo(
    () => buildGeneralChecks(title, excerpt, content, words, featuredImage),
    [title, excerpt, content, words, featuredImage]
  );

  const perKeyword = useMemo(
    () =>
      keywords.map((kw) => ({
        keyword: kw,
        checks: buildKeywordChecks(kw, { title, slug, excerpt, text, contentHtml: content, words }),
      })),
    [keywords, title, slug, excerpt, text, content, words]
  );

  const allChecks = useMemo(
    () => [...generalChecks, ...perKeyword.flatMap((p) => p.checks)],
    [generalChecks, perKeyword]
  );

  const score = keywords.length === 0 ? 0 : scoreOf(allChecks);
  const scoreColor =
    score >= 75 ? "text-green-500" : score >= 50 ? "text-yellow-500" : "text-red-500";
  const scoreLabel = score >= 75 ? "Good" : score >= 50 ? "OK" : "Needs work";

  return (
    <div className="border border-border rounded-lg bg-card p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">SEO Analysis</h3>
          <p className="text-xs text-muted-foreground">
            {keywords.length === 0
              ? "Add at least one focus keyword above."
              : `Analyzing ${keywords.length} keyword${keywords.length === 1 ? "" : "s"}.`}
          </p>
        </div>
        {keywords.length > 0 && (
          <div className="text-right">
            <div className={`text-2xl font-bold ${scoreColor}`}>{score}</div>
            <div className="text-xs text-muted-foreground">{scoreLabel}</div>
          </div>
        )}
      </div>

      <div className="mb-4">
        <h4 className="text-sm font-semibold text-foreground mb-2">General</h4>
        <CheckList checks={generalChecks} />
      </div>

      {perKeyword.map(({ keyword, checks }) => {
        const kwScore = scoreOf(checks);
        const color =
          kwScore >= 75 ? "text-green-500" : kwScore >= 50 ? "text-yellow-500" : "text-red-500";
        return (
          <div key={keyword} className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-foreground">
                Keyword: <span className="text-primary">{keyword}</span>
              </h4>
              <span className={`text-sm font-semibold ${color}`}>{kwScore}</span>
            </div>
            <CheckList checks={checks} />
          </div>
        );
      })}
    </div>
  );
};

export default SeoAnalyzer;
